import queryString from 'query-string';

import {
  ROLE_PREVIEW_PARAM,
  resolveRolePreviewOverride,
  setRolePreviewOverride,
} from '../Auth/rbac/rolePreviewOverride';
import {
  STARTER_PLAN_PARAM,
  resolveStarterPlanOverride,
  setStarterPlanOverride,
} from '../entitlements/starterPreviewOverride';
import history from '../historyUtil';

/**
 * The URL half of the two support-only preview overrides (`?preview_role=`, `?preview_plan=`).
 *
 * Lives here, apart from the modules that own each axis, for one hard reason: stripping
 * the param correctly requires the app's `history` singleton (see `stripParam` below),
 * and `src/historyUtil.ts` calls `createBrowserHistory()` at module scope, which throws
 * "Browser history needs a DOM" under the node test project. `rolePreviewOverride.ts` and
 * `starterPreviewOverride.ts` are both imported by node tests that exercise their pure
 * resolvers, so neither may reach `historyUtil`. This module is imported only from the
 * jsdom-only hooks (`useRole`, `useEntitlement`), so it may.
 *
 * Both axes share this file rather than one each: what is shared is not the preview
 * semantics — those stay wholly in each axis's own module, including its param name and
 * its allow-list — but a single piece of DOM/router plumbing that neither axis should own
 * a private copy of.
 */

/**
 * Rewrites the address bar without `param`, through the ROUTER'S OWN history.
 *
 * NOT `window.history.replaceState`, which is what this used to be and what made the
 * strip cosmetic. `history@4` (`createBrowserHistory`) caches its own `location` and
 * resyncs it only on `popstate`; a raw `replaceState` fires no `popstate`, so the router
 * went on believing the param was still there. `use-query-params` builds every
 * `pushIn`/`replaceIn` write by merging into the ROUTER's location, so the very next
 * query-param write anywhere in the app — a tab switch, a date change, a filter — put the
 * param straight back into the URL. Measured: strip leaves `?keep=1`, one later write
 * yields `?preview_role=admin&keep=1&tab=performance`. That resurrection is exactly what
 * defeats "My real seat": clearing storage re-runs `promote…FromUrl`, which finds the
 * param again and re-stores it, so the preview can never be switched off.
 *
 * `history.replace` updates the router's cached location and the address bar together, so
 * there is nothing left to re-inject. `replace`, not `push`: this is a URL cleanup, not a
 * destination, and it must not add a back-button entry — the property the raw
 * `replaceState` was correctly reaching for.
 *
 * Nudging the router with a synthetic `PopStateEvent` would be the other way to resync it,
 * and is deliberately not used: this app arms `history.block()` for its unsaved-changes
 * guard (`setNavigationConfirmation` in `src/historyUtil.ts`), and a fabricated popstate
 * runs through that machinery with no real navigation behind it.
 *
 * Importing the singleton does NOT require a rendered `<Router>` — it is just a module —
 * so the hooks keep working in Storybook and in tests that mount no router at all.
 */
function stripParam(param: string): void {
  const parsed = queryString.parse(window.location.search);
  if (!(param in parsed)) return;

  delete parsed[param];
  const query = queryString.stringify(parsed);
  const { pathname, hash } = window.location;
  history.replace({
    pathname,
    search: query ? `?${query}` : '',
    hash,
    // Whatever the current entry was carrying is somebody else's state, not ours.
    state: history.location?.state,
  });
}

/**
 * Moves a `preview_role` URL param into localStorage, then strips it from the address bar.
 *
 * Without this the URL stays a live, higher-priority source for the whole session, so a
 * link opened with `?preview_role=readonly` can never be switched off from inside the app:
 * "My real seat" clears localStorage, the resolver still finds the param, and the radio
 * snaps back. `use-query-params` merges the current search into every navigation, so the
 * param rides along even to screens that know nothing about it. Promoting makes the
 * setter authoritative, and makes the value survive client-side navigation instead of
 * silently differing between components mounted before and after it.
 *
 * The param is stripped even when it is unusable (`?preview_role=support`, a typo) — the
 * preview owns the param entirely, so leaving a rejected one behind only invites someone
 * to wonder why it does nothing.
 *
 * The CALLER must have verified the real role is support (`useIsRealSupport`): promoting
 * is a WRITE, and an ordinary user's param must be ignored outright, not filed away
 * where it would take effect the moment they are granted support.
 */
export function promoteRolePreviewOverrideFromUrl(): void {
  const { search } = window.location;
  if (!(ROLE_PREVIEW_PARAM in queryString.parse(search))) return;

  // No `stored` argument on purpose: this must read the URL alone, so an existing stored
  // value is never re-written over itself by a param that the allow-list rejected.
  const fromUrl = resolveRolePreviewOverride({ search });

  // Strip BEFORE storing. The setter dispatches its CustomEvent synchronously, and every
  // subscribed hook's listener calls back into here — with the param still in the URL, that
  // recursed until the stack blew. `fromUrl` is already resolved from the captured `search`,
  // so stripping first changes nothing but the re-entrant call's early return.
  stripParam(ROLE_PREVIEW_PARAM);
  if (fromUrl !== null) setRolePreviewOverride(fromUrl);
}

/**
 * Moves a `preview_plan` URL param into localStorage, then strips it from the address bar.
 *
 * Without this the URL stays a live, higher-priority source for the whole session, so a
 * link opened with `?preview_plan=starter` can never be switched off from inside the
 * app: the Account switch clears localStorage, the resolver still finds the param, and the
 * switch snaps back on. `use-query-params` merges the current search into every navigation, so
 * the param rides along even to screens that know nothing about it. Promoting makes the
 * setter authoritative, and makes the value survive client-side navigation instead of
 * silently differing between components mounted before and after it.
 *
 * The param is stripped even when it is unusable (`?preview_plan=premium`, a typo) — the preview
 * owns the param entirely, so a rejected one left behind only invites someone to wonder
 * why it does nothing.
 *
 * The CALLER must have verified the real role is support (`useIsRealSupport`): promoting
 * is a WRITE, and an ordinary user's param must be ignored outright, not filed away
 * where it would take effect the moment they are granted support.
 *
 * Deliberately a sibling of `promoteRolePreviewOverrideFromUrl` rather than one generic
 * function over both: each axis keeps its own param name, allow-list and resolver, the
 * same separation that keeps `PERMISSIONS` and `FEATURES` independent.
 */
export function promoteStarterPlanOverrideFromUrl(): void {
  const { search } = window.location;
  if (!(STARTER_PLAN_PARAM in queryString.parse(search))) return;

  const fromUrl = resolveStarterPlanOverride({ search });

  // Strip before storing — see `promoteRolePreviewOverrideFromUrl` above.
  stripParam(STARTER_PLAN_PARAM);
  if (fromUrl !== null) setStarterPlanOverride(fromUrl);
}
